import { useCallback, useEffect, useMemo, useState } from 'react'
import { renderCellData } from 'Components/Stats/ResultsTable/util.js'
import { defaultSort } from '../../../../../Utils/Table'

const formatLabel = str => {
  if (str.includes('-cvr')) {
    return str.split('-')[0] + ' CVR'
  } else if (str.includes('-cps')) {
    return 'Cost Per ' + str.split('-')[0]
  } else if (str === 'FTE') {
    return str
  } else {
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim()
  }
}

export const formatChartData = (item, currencySign) => {
  if (item && item.chartId) {
    const data = { ...item }
    delete data.chartId

    return Object.entries(data).map(([key, value]) => [
      formatLabel(key),
      renderCellData(value, key.includes('-cvr'), key, currencySign),
    ])
  } else {
    return []
  }
}

export const useChartsLogic = props => {
  const {
    data,
    chartWeeks,
    chartMetrics,
    chartOptions,
    isHideTimeRangeSelects,
  } = props

  const metrics = useMemo(
    () =>
      chartMetrics.reduce((acc, item) => {
        if (item !== 'scans') {
          acc.push({
            label: formatLabel(item),
            value: item,
          })
        }
        return acc
      }, []),
    [chartMetrics],
  )

  const [form, setForm] = useState({})

  const initOptions = useMemo(
    () => ({
      title: {
        text: '',
      },
      rangeSelector: {
        selected: 1,
      },
      navigator: {
        enabled: false,
      },
      yAxis: {
        title: {
          text: '',
        },
      },
      legend: {
        align: 'center',
        verticalAlign: 'top',
        borderWidth: 10,
        borderColor: 'transparent',
      },
      ...chartOptions,
    }),
    [chartOptions],
  )

  const { weekFrom, weekTo } = useMemo(() => {
    const weekFromRange = chartWeeks.slice(0, chartWeeks.indexOf(form.weekTo))
    const weekToRange = chartWeeks.slice(
      chartWeeks.indexOf(form.weekFrom) + 1,
      chartWeeks.length + 1,
    )

    const weekFrom = weekFromRange.map(item => ({
      label: item,
      value: item,
    }))

    const weekTo = weekToRange.map(item => ({
      label: item,
      value: item,
    }))

    return { weekFrom, weekTo }
  }, [form.weekFrom, form.weekTo])

  const [options, setOptions] = useState(initOptions)

  useEffect(() => {
    if (!form.to && !form.from && !form.metric) {
      setForm(prev => ({
        weekFrom: chartWeeks[0],
        weekTo: chartWeeks[chartWeeks.length - 1],
        metric: chartMetrics[0],
      }))

      setOptions(() => ({
        ...initOptions,
        series: data.coordinateY.map(item => ({
          ...item,
          data: item.data.map(item => item[chartMetrics[0]]),
        })),
      }))
    }

    if (form.weekTo && form.weekFrom && form.metric) {
      if (isHideTimeRangeSelects) {
        setOptions(prev => ({
          ...prev,
          series: data.coordinateY
            .map(item => ({
              ...item,
              data: item.data.map(item => item[form.metric]),
            }))
            .reverse()
            .sort((a, b) => defaultSort(a.name, b.name)),
        }))
      } else {
        const rangeDatesList = chartWeeks.slice(
          chartWeeks.indexOf(form.weekFrom),
          chartWeeks.indexOf(form.weekTo) + 1,
        )

        setOptions(prev => ({
          ...prev,
          xAxis: {
            categories: rangeDatesList,
          },
          series: data.coordinateY.map(item => ({
            ...item,
            data: item.data.reduce((acc, item) => {
              if (rangeDatesList.includes(item.week)) {
                if (item.chartId) {
                  acc.push(item[form.metric])
                } else {
                  acc.push(0)
                }
              }
              return acc
            }, []),
          })),
        }))
      }
    }
  }, [chartWeeks, initOptions, form.weekTo, form.weekFrom, form.metric])

  const handleFormChange = useCallback(item => {
    if (item.isDirty) {
      setForm(prev => ({ ...prev, [item.name]: item.value }))
    }
  }, [])

  return { options, metrics, handleFormChange, weekFrom, weekTo, form }
}



// WEBPACK FOOTER //
// ./src/Containers/StatsOverview/components/HighchartsCharts/utils/index.js